import DownSvg from 'assets/svg/Down';
import TileButton, { tileDataProp } from 'components/atoms/TileButton';
import Legend from 'components/molecules/Legend';
import { Tooltip } from 'antd';
import { CustomSelect } from 'components/pages/GrowthAnalysis';
import {
    graphItemContainerClass,
    subHeaderClass,
    yAxisLabelClass,
} from 'components/template/GrowthAnalysis/ExpandedView';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import React, { FC, Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    calculateContainerHeight,
    calculateFontSize,
    fetchCurrency,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    ellipseMiddleString,
    getAdditionalDataOnCopyClick,
    getConfig,
    parseAttributeType,
    scaleMaxValue,
    scaleMinValue,
    getKeyByValue,
} from 'utils/helperMethods';
import { legendData } from './SalesChartContainer/SalesChartContainer';
import Barchart from 'components/molecules/Barchart';
import StackedBarChart from 'components/molecules/StackedBarChart';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import _ from 'lodash';
import { colorConstants } from 'config/colorConstants';
import { AutoSizer, List } from 'react-virtualized';
import { useFetchSalesAttributionFeatureLevel } from 'hooks/growthAnalysis/useFetchSalesAttributionFeatureLevelChart';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import { useFetchAttributionFeatureDimensionLevel } from 'hooks/growthAnalysis/useFetchAttributionFeatureDimensionLevel';
import CopyToClipBoard, { classToRemoveScrollbar } from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { drilldownChartData, drilldownBreakdownChartData } from 'parser/graphToExcelDownloadParser';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    GROWTH_ANALYSIS_SALES_CHART_SUB_FACTOR_CLICKED,
    GROWTH_ANALYSIS_SALES_CHART_SUB_FACTOR_DRILLDOWN_ATTRIBUTE_DROPDOWN_CHANGED,
    GROWTH_ANALYSIS_SALES_CHART_SUB_FACTOR_DRILLDOWN_CLICKED,
    GROWTH_ANALYSIS_SALES_CHART_SUB_FACTOR_DRILLDOWN_TAB_CHANGED,
    GROWTH_ANALYSIS_SALES_CHART_SUB_FACTOR_TAB_CHANGED,
} from 'utils/mixpanelEventNames';

interface Props {
    setSecondChartItem: Function;
    secondChartItem: any;
    selectedAttribute: string;
    expandedItem: any;
    stringifiedFilter: string;
    volTile: tileDataProp;
    firstChartItem: any;
    setFirstChartItem: Function;
    selectPPG: any;
    setSelectPPG: Function;
    attributeTypesList: any[];
    setAttributeTypesList: Function;
    timePeriod: string | string[];
    comparePeriodEnabled: boolean;
    comparisonPeriodType?: string;
    comparePeriodLabel: string;
}
export const graphXAxisLabelFontSize = calculateFontSize(window.innerWidth, 13);
const yAxisGraphWidth = calculateFontSize(window.innerWidth, 120);
const chart2ContainerId = 'chart-2-sales-attribution-drillDown';
const height = Math.max(450, calculateContainerHeight(2, 33 + 16 * 2) - 200); //adding a buffer of 200

export const commonYAxisStyle: any = {
    width: yAxisGraphWidth,
    minWidth: yAxisGraphWidth,
    overflowWrap: 'anywhere',
    fontSize: graphXAxisLabelFontSize,
};

const SalesAttributionDrillDownChart: FC<Props> = ({
    expandedItem,
    selectedAttribute,
    setSecondChartItem,
    secondChartItem,
    firstChartItem,
    stringifiedFilter,
    volTile,
    setFirstChartItem,
    selectPPG,
    setSelectPPG,
    attributeTypesList,
    setAttributeTypesList,
    timePeriod,
    comparePeriodEnabled,
    comparisonPeriodType,
    comparePeriodLabel,
}) => {
    const chartLabel = `vs ${comparePeriodLabel} :`;
    const { appConfig } = useContext(UserContext);
    const growthAnalysisConfig = getConfig('growthAnalysis', appConfig);
    const salesAttributionChart2DisabledItems = growthAnalysisConfig?.salesAttributionChart2DisabledItems;
    const includeAggPeriod = growthAnalysisConfig?.includeAggPeriod;
    const salesAttributionDrillDownChartKeyValueMapping =
        growthAnalysisConfig.salesAttributionDrillDownChartKeyValueMapping || {};
    const commonConfig = getConfig('commonConfig', appConfig);
    const globalFilterConfig = getConfig('globalFilter', appConfig);
    const replacementKeyMapping = globalFilterConfig?.replacementKeyMapping;
    const showFullLabel = commonConfig?.showFullLabel;
    const decimalForPercentage = commonConfig?.decimalForPercentage ?? 2;
    const decimalForNonPercentage = commonConfig?.decimalForNonPercentage ?? 2;
    const { absTileData } = growthAnalysisConfig;
    const [absTile, setAbsTile] = useState([absTileData[0], absTileData[0]]);
    const currency = volTile?.key === 'val' ? fetchCurrency(appConfig) : '';

    const {
        allDimAndAttributeTypeList,
        timePeriodSelection,
        allCheckedAttribute,
        currentFrequency,
        screenFilterConfig,
        timeSelectionLabel,
    } = useContext(FilterContextData);

    const chart2OffsetRef = useRef(0);
    const chart1Ref = useRef<any>();
    const chart2Ref = useRef<any>();

    const {
        data: chart1Data,
        loading: chart1Loading,
        error: chart1Error,
        fetchAttributeSales: fetchChart1Data,
    } = useFetchSalesAttributionFeatureLevel();

    const {
        data: chart2Data,
        loading: chart2Loading,
        error: chart2Error,
        fetchAttributeSales: fetchChart2Data,
        count: chart2Count,
        isBreakdown,
    } = useFetchAttributionFeatureDimensionLevel();

    const containerWidth = useContainerWidth({
        containerId: chart2ContainerId + 'q', //giving an incorrect id so that it takes from main container by default
        buffer: 16 * 3 + 2, //padding 16 * 3 + border 2
    });

    const chartWidth = containerWidth / 2 - 16 * 3; //subtract inner padding for 2 charts

    const setTile = (index: number, item: any) => {
        absTile.splice(index, 1, item);
        setAbsTile([...absTile]);
    };
    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !selectPPG
        ) {
            const data = parseAttributeType(allDimAndAttributeTypeList['dimentions']);
            const dim1Attributes = allDimAndAttributeTypeList['dimentions'][0];
            if (data.length) {
                setAttributeTypesList(data);
                setSelectPPG(data[dim1Attributes.attribute.length - 1]); // select last level from dim1 attributes as default
            }
        }
    }, [allDimAndAttributeTypeList?.loading]);

    //common variable used for api call
    const commonVariables = useMemo(() => {
        return {
            featureGroupName: _.snakeCase(selectedAttribute),
            contributionType: volTile?.dataKey?.toLowerCase(),
            filter: stringifiedFilter,
            timePeriod,
            frequency: currentFrequency?.key,
            featureName: screenFilterConfig?.dimensionListKey ?? 'default',
            comparisonPeriodType,
            aggPeriod:
                comparePeriodEnabled || !includeAggPeriod
                    ? ''
                    : String(timeSelectionLabel)?.toLowerCase() === 'custom date' ||
                      String(timeSelectionLabel)?.toLowerCase() === 'ytd'
                    ? ''
                    : getKeyByValue(replacementKeyMapping, String(timeSelectionLabel)?.toUpperCase()), // String(timeSelectionLabel)?.toLowerCase() === 'custom date' ||
            // String(timeSelectionLabel)?.toLowerCase() === 'ytd'
            //     ? ''
            //     : getKeyByValue(replacementKeyMapping, String(timeSelectionLabel)?.toUpperCase()),
            lagList: commonConfig.lagList,
        };
    }, [timePeriod, timeSelectionLabel, volTile, selectedAttribute, screenFilterConfig, stringifiedFilter]);

    //1st chart api
    useEffect(() => {
        if (selectedAttribute) {
            fetchChart1Data({
                variables: {
                    ...commonVariables,
                },
            });
        }
    }, [selectedAttribute]);

    useEffect(() => {
        if (chart1Data && !chart1Loading) {
            setFirstChartItem(chart1Data?.data?.[absTile[0]?.key]?.[0]);
        }
    }, [chart1Data]);

    //2nd chart api
    useEffect(() => {
        if (firstChartItem && selectPPG) {
            setSecondChartItem(null);
            chart2OffsetRef.current = 0;
            fetchChart2Data({
                variables: {
                    ...commonVariables,
                    attributeFeatureName:
                        firstChartItem?.category in salesAttributionDrillDownChartKeyValueMapping
                            ? salesAttributionDrillDownChartKeyValueMapping[firstChartItem?.category]
                            : _.snakeCase(firstChartItem?.category),
                    attributeType: selectPPG?.key,
                    dimension: selectPPG?.dimension,
                    limit: growthAnalysisConfig.salesAttributionChart2Limit,
                    offset: chart2OffsetRef.current,
                },
            });
        }
    }, [firstChartItem, selectPPG]);

    //chart item click handler
    const chartItemOnClick = (stateItem: any, item: any, setState: Function, allowToggle: boolean = false) => {
        //toggle logic
        if (allowToggle && item?.category === stateItem?.category) {
            return setState(null);
        }

        //reset 2nd chart when 1sy chart is changed
        if (stateItem === firstChartItem && item?.category !== stateItem?.category) {
            setSecondChartItem(null);
        }
        setState(item);
    };

    const combinedLoading = chart1Loading || chart2Loading;

    //chart 1 label
    const formattedChartOneLabel = `${_.startCase(selectedAttribute)} ${chartLabel} ${
        expandedItem?.attributeExId || ''
    }`;

    //chart 2 label
    const formattedChartTwoLabel = `${firstChartItem?.category} ${chartLabel} ${expandedItem?.attributeExId || ''}`;
    //bar graph height
    const barHeight1 = Math.max(
        77,
        Array.isArray(chart1Data?.data?.[absTile[0].key]) && chart1Data?.data?.[absTile[0].key]?.length > 0
            ? height / chart1Data?.data?.[absTile[0].key]?.length
            : 0,
    );
    //scale y axis w.r.t the max characters to avoid collision of names  (only for chart 2)
    const barHeight2WRTCharacters = Math.max(chart2Data?.maxCharacters * (77 / 39) || 0, 77); // 39 characters can be accommodated in 77px height
    const barHeight2 = Math.max(
        barHeight2WRTCharacters,
        Array.isArray(chart2Data?.data?.[absTile[1].key]) && chart2Data?.data?.[absTile[1].key]?.length > 0
            ? height / chart2Data?.data?.[absTile[1].key]?.length
            : 0,
    );

    //infinite scroll function
    function infiniteScrollOnNext(e: any) {
        //finding the scrollbar position
        const scrollHeight = e?.scrollHeight; //total height
        const clientHeight = e?.clientHeight; //viewport height
        const scrollBarBottomPosition = clientHeight + e?.scrollTop;
        const threshold = 0.85;
        const currentDataLength = chart2Data?.data?.[absTile[1].key]?.length || 0;

        //only infinite scroll if current data length is less than total meta data
        //if loading is false
        if (
            scrollBarBottomPosition >= scrollHeight * threshold &&
            currentDataLength < chart2Count &&
            !combinedLoading
        ) {
            // TODO: Temp fix for now
            // Derive offset from actual data length rather than the tracked ref.
            chart2OffsetRef.current = currentDataLength; // kept in sync for scrollToIndex
            fetchChart2Data({
                variables: {
                    ...commonVariables,

                    attributeFeatureName:
                        firstChartItem?.category in salesAttributionDrillDownChartKeyValueMapping
                            ? salesAttributionDrillDownChartKeyValueMapping[firstChartItem?.category]
                            : _.snakeCase(firstChartItem?.category),
                    attributeType: selectPPG?.key,
                    dimension: selectPPG?.dimension,
                    limit: growthAnalysisConfig.salesAttributionChart2Limit,
                    offset: currentDataLength,
                },
            });
        }
    }
    //debounced infinite scroller function
    const debouncedInfiniteScroller = useCallback(_.debounce(infiniteScrollOnNext, 150), [
        chart2Count,
        chart2Data,
        combinedLoading,
    ]);

    const chart1ExcelData = useMemo(() => {
        return drilldownChartData(
            chart1Data?.data?.[absTile[0]?.key],
            _.startCase(selectedAttribute),
            `Sales ${absTile[0]?.label}`,
        );
    }, [chart1Data?.data, absTile]);

    const chart2ExcelData = useMemo(() => {
        const key = absTile[1]?.key;
        if (isBreakdown && chart2Data?.breakdown?.[key]) {
            return drilldownBreakdownChartData(
                chart2Data?.data?.[key],
                chart2Data?.breakdown?.[key],
                selectPPG?.label,
                `Sales ${absTile[1]?.label}`,
            );
        }
        return drilldownChartData(chart2Data?.data?.[key], selectPPG?.label, `Sales ${absTile[1]?.label}`);
    }, [chart2Data?.data, chart2Data?.breakdown, absTile, isBreakdown]);

    return (
        <section className="flex gap-4">
            {/* CHART 1 CONTAINER */}
            <div className={`${graphItemContainerClass} mb-0 flex-1`}>
                {/* ERROR */}
                {chart1Error && !chart1Loading && !chart1Data ? (
                    <div
                        style={{ height: height + 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <ErrorBox msg={String(chart1Error)} />
                    </div>
                ) : // LOADING
                chart1Loading ? (
                    <div style={{ height: height + 50 }}>
                        <Loader />
                    </div>
                ) : (
                    <div ref={chart1Ref}>
                        <div className="flex justify-between items-center">
                            <p className={subHeaderClass}>{formattedChartOneLabel}</p>
                            <TileButton
                                data={absTileData}
                                activeKey={absTile[0].key}
                                onClick={(item: any) => {
                                    setTile(0, item);

                                    //mixpanel event
                                    trackEventMixpanel(GROWTH_ANALYSIS_SALES_CHART_SUB_FACTOR_TAB_CHANGED, {
                                        tabName: item?.label,
                                    });
                                }}
                            />
                        </div>
                        <div className="flex justify-between items-center" style={{ marginTop: 14, marginBottom: 24 }}>
                            <div className="w-[35px]"></div>
                            <Legend data={legendData} />

                            <div className="flex items-center justify-end gap-2">
                                <CopyToClipBoard removeScrollbar refOfComponent={chart1Ref} />
                                <DownloadExcel
                                    title={'DrilldownChart1'}
                                    titleArray={[[formattedChartOneLabel]]}
                                    excelData={chart1ExcelData.data}
                                    headers={chart1ExcelData.headers}
                                />
                            </div>
                        </div>
                        <div style={{ height }} className={`${classToRemoveScrollbar} overflow-auto`}>
                            {chart1Data?.data?.[absTile[0]?.key]?.map((item: any, index: number) => {
                                return (
                                    <div
                                        onClick={() => {
                                            chartItemOnClick(firstChartItem, item, setFirstChartItem, false);

                                            //mixpanel event
                                            trackEventMixpanel(GROWTH_ANALYSIS_SALES_CHART_SUB_FACTOR_CLICKED, {
                                                subFactorName: item?.category,
                                            });
                                        }}
                                        style={{
                                            background:
                                                firstChartItem?.category === item?.category
                                                    ? colorConstants.grey
                                                    : 'none',
                                        }}
                                        className={
                                            'relative flex items-center ' +
                                            (item?.category !== firstChartItem?.category ? 'cursor-pointer' : '')
                                        }
                                    >
                                        <div style={{ ...commonYAxisStyle }} className="text-center p-[2px]">
                                            {showFullLabel ? (
                                                item?.category
                                            ) : (
                                                <Tooltip
                                                    placement="top"
                                                    color="white"
                                                    destroyTooltipOnHide
                                                    overlayStyle={{ borderRadius: 4 }}
                                                    overlayInnerStyle={{
                                                        fontSize: graphXAxisLabelFontSize,
                                                        color: colorConstants.darkGrey,
                                                    }}
                                                    title={item?.category}
                                                >
                                                    <div>{ellipseMiddleString(item?.category, 8, 4)}</div>
                                                </Tooltip>
                                            )}
                                        </div>
                                        <Barchart
                                            offsetXDistance={-6}
                                            padding={[0, 10, 0, 10]}
                                            barWidth={30}
                                            //yAxisConfig={false}
                                            dataset={[item]}
                                            height={barHeight1}
                                            loading={false}
                                            chartContainer={`attribution-drillDown-1-${index}`}
                                            fields={['category', 'sales']}
                                            intervalLabelTooltipKey={'labelSales'}
                                            minBound={scaleMinValue(50, chart1Data?.minVal?.[absTile[0]?.key])}
                                            maxBound={scaleMaxValue(50, chart1Data?.maxVal?.[absTile[0]?.key])}
                                            width={chartWidth - yAxisGraphWidth}
                                            transpose
                                            intervalFormatter={(val: any) => {
                                                return absTile[0].key === 'percentage'
                                                    ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                                    : currency +
                                                          formatNumWithKMBAlt(
                                                              val,
                                                              decimalForNonPercentage,
                                                              decimalForNonPercentage,
                                                          );
                                            }}
                                            tooltipFormatter={(xAxis, val) => {
                                                return {
                                                    name: xAxis,
                                                    value:
                                                        absTile[0].key === 'percentage'
                                                            ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                                            : currency +
                                                              formatNumWithKMBAlt(
                                                                  val,
                                                                  decimalForNonPercentage,
                                                                  decimalForNonPercentage,
                                                              ),
                                                };
                                            }}
                                            // Yline={false}
                                            Xline={false}
                                            colorKey={'color'}
                                            intervalStyle={
                                                item?.category !== firstChartItem?.category ? { cursor: 'pointer' } : {}
                                            }
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        {/* X AXIS GRAPH COMPONENT */}
                        <Barchart
                            additionalStyle={{ marginLeft: yAxisGraphWidth - 10 }}
                            padding={[0, 10, 20, 10]}
                            //yAxisConfig={false}
                            dataset={[]}
                            height={50}
                            loading={false}
                            chartContainer={`attribution-drillDown-X-1-${1}`}
                            fields={['category', 'sales']}
                            intervalLabelTooltipKey={'labelSales'}
                            minBound={scaleMinValue(50, chart1Data?.minVal?.[absTile[0]?.key])}
                            maxBound={scaleMaxValue(50, chart1Data?.maxVal?.[absTile[0]?.key])}
                            width={chartWidth - yAxisGraphWidth + 20}
                            transpose
                            yAxisLabelFormatter={(val: any) => {
                                return absTile[0]?.key === 'percentage'
                                    ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                    : formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                            }}
                            // Yline
                            YGrid={false}
                            xAxisConfig={false}
                            colorKey={'color'}
                        />
                        <XAxisLabel absTile={volTile} appConfig={appConfig} />
                    </div>
                )}
            </div>
            {/* CHART 2 CONTAINER */}
            {/* ONLY SHOW 2ND CHART WHEN FIRST CHART IS SELECTED */}

            <div className={`${graphItemContainerClass} mb-0 flex-1`}>
                {chart2Error && !combinedLoading && !chart2Data ? (
                    <div
                        style={{ height: height + 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <ErrorBox msg={String(chart2Error)} />
                    </div>
                ) : // LOADING
                combinedLoading ? (
                    <div style={{ height: height + 50 }}>
                        <Loader />
                    </div>
                ) : (
                    <div ref={chart2Ref}>
                        <div className="flex justify-between">
                            <p className={subHeaderClass}>{formattedChartTwoLabel}</p>
                            <div className="flex gap-2 items-center">
                                <CustomSelect
                                    suffixIcon={<DownSvg />}
                                    disabled={combinedLoading}
                                    value={selectPPG?.value}
                                    options={attributeTypesList}
                                    className="dropdown-height dropdown-width uniqSelPVMSubFactorDropdown"
                                    onChange={(val: any, item: any) => {
                                        setSelectPPG(item);

                                        //mixpanel event
                                        trackEventMixpanel(
                                            GROWTH_ANALYSIS_SALES_CHART_SUB_FACTOR_DRILLDOWN_ATTRIBUTE_DROPDOWN_CHANGED,
                                            {
                                                attributeType: item?.label,
                                            },
                                        );
                                    }}
                                />
                                <TileButton
                                    data={absTileData}
                                    activeKey={absTile[1].key}
                                    onClick={(item: any) => {
                                        setTile(1, item);
                                        //mixpanel event
                                        trackEventMixpanel(
                                            GROWTH_ANALYSIS_SALES_CHART_SUB_FACTOR_DRILLDOWN_TAB_CHANGED,
                                            {
                                                tabName: item?.label,
                                            },
                                        );
                                    }}
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between" style={{ marginTop: 14, marginBottom: 24 }}>
                            <div className="w-[35px]"></div>
                            <Legend data={legendData} />

                            <div className="flex items-center justify-end gap-2">
                                <CopyToClipBoard
                                    removeScrollbar
                                    elementClassToRemoveScrollbar="ReactVirtualized__Grid ReactVirtualized__List"
                                    refOfComponent={chart2Ref}
                                />
                                <DownloadExcel
                                    titleArray={[[formattedChartOneLabel], [formattedChartTwoLabel]]}
                                    title={'DrilldownChart2'}
                                    excelData={chart2ExcelData.data}
                                    headers={chart2ExcelData.headers}
                                />
                            </div>
                        </div>
                        <>
                            <div style={{ height }}>
                                <div style={{ width: '100%', height: height }}>
                                    <AutoSizer>
                                        {({ width, height }) => {
                                            return (
                                                <div className="App">
                                                    <List
                                                        scrollToIndex={Math.max(0, chart2OffsetRef.current - 1)}
                                                        onScroll={debouncedInfiniteScroller}
                                                        width={width}
                                                        height={height}
                                                        rowHeight={barHeight2}
                                                        rowCount={chart2Data?.data?.[absTile[1]?.key]?.length || 0}
                                                        rowRenderer={({ key, index, style }) => {
                                                            const item = chart2Data?.data?.[absTile[1]?.key]?.[index];
                                                            const disableClickEvent =
                                                                salesAttributionChart2DisabledItems?.includes(
                                                                    firstChartItem?.category?.toLowerCase(),
                                                                );
                                                            const breakdownChartData = isBreakdown
                                                                ? chart2Data?.breakdown?.[absTile[1]?.key]?.[
                                                                      item?.category
                                                                  ]
                                                                : null;
                                                            return (
                                                                <div key={key} style={style}>
                                                                    <div
                                                                        onClick={() => {
                                                                            if (disableClickEvent) return;
                                                                            chartItemOnClick(
                                                                                secondChartItem,
                                                                                item,
                                                                                setSecondChartItem,
                                                                                true,
                                                                            );
                                                                            trackEventMixpanel(
                                                                                GROWTH_ANALYSIS_SALES_CHART_SUB_FACTOR_DRILLDOWN_CLICKED,
                                                                                {
                                                                                    subFactorName: item?.category,
                                                                                },
                                                                            );
                                                                        }}
                                                                        className={
                                                                            'relative flex items-center ' +
                                                                            (!disableClickEvent ? 'cursor-pointer' : '')
                                                                        }
                                                                        style={{
                                                                            background:
                                                                                secondChartItem?.category ===
                                                                                item?.category
                                                                                    ? colorConstants.grey
                                                                                    : 'none',
                                                                        }}
                                                                    >
                                                                        <div
                                                                            title={item?.category}
                                                                            style={{
                                                                                ...commonYAxisStyle,
                                                                                wordBreak: 'break-all',
                                                                            }}
                                                                            className="text-center p-[2px]"
                                                                        >
                                                                            {showFullLabel ? (
                                                                                item?.category
                                                                            ) : (
                                                                                <Tooltip
                                                                                    placement="top"
                                                                                    color="white"
                                                                                    destroyTooltipOnHide
                                                                                    overlayStyle={{
                                                                                        borderRadius: 4,
                                                                                    }}
                                                                                    overlayInnerStyle={{
                                                                                        fontSize:
                                                                                            graphXAxisLabelFontSize,
                                                                                        color: colorConstants.darkGrey,
                                                                                    }}
                                                                                    title={item?.category}
                                                                                >
                                                                                    <div>
                                                                                        {ellipseMiddleString(
                                                                                            item?.category,
                                                                                            8,
                                                                                            4,
                                                                                        )}
                                                                                    </div>
                                                                                </Tooltip>
                                                                            )}
                                                                        </div>
                                                                        {isBreakdown && breakdownChartData ? (
                                                                            <StackedBarChart
                                                                                padding={[0, 10, 0, 20]}
                                                                                barWidth={30}
                                                                                dataset={breakdownChartData}
                                                                                height={barHeight2}
                                                                                loading={false}
                                                                                chartContainer={`attribution-drillDown-2-${index}`}
                                                                                fields={['row', 'value', 'segment']}
                                                                                colorKey={'color'}
                                                                                disableIntervalLabel
                                                                                minBound={scaleMinValue(
                                                                                    50,
                                                                                    chart2Data?.minVal?.[
                                                                                        absTile[1]?.key
                                                                                    ],
                                                                                )}
                                                                                maxBound={scaleMaxValue(
                                                                                    50,
                                                                                    chart2Data?.maxVal?.[
                                                                                        absTile[1]?.key
                                                                                    ],
                                                                                )}
                                                                                tooltipFormatter={(xAxis, val) => {
                                                                                    return {
                                                                                        name: xAxis,
                                                                                        value:
                                                                                            absTile[1].key ===
                                                                                            'percentage'
                                                                                                ? formatNumWithKMBAlt(
                                                                                                      val,
                                                                                                      decimalForPercentage,
                                                                                                  ) + '%'
                                                                                                : currency +
                                                                                                  formatNumWithKMBAlt(
                                                                                                      val,
                                                                                                      decimalForNonPercentage,
                                                                                                      decimalForNonPercentage,
                                                                                                  ),
                                                                                    };
                                                                                }}
                                                                                extras={{
                                                                                    data: {
                                                                                        [item?.category]: {
                                                                                            total:
                                                                                                absTile[1].key ===
                                                                                                'percentage'
                                                                                                    ? formatNumWithKMBAlt(
                                                                                                          item.sales,
                                                                                                          decimalForPercentage,
                                                                                                      ) + '%'
                                                                                                    : currency +
                                                                                                      formatNumWithKMBAlt(
                                                                                                          item.sales,
                                                                                                          decimalForNonPercentage,
                                                                                                          decimalForNonPercentage,
                                                                                                      ),
                                                                                        },
                                                                                    },
                                                                                    dataMapping: {
                                                                                        total: { label: 'Total Sales' },
                                                                                    },
                                                                                }}
                                                                                width={chartWidth - yAxisGraphWidth}
                                                                                transpose
                                                                                Xline={false}
                                                                            />
                                                                        ) : (
                                                                            <Barchart
                                                                                offsetXDistance={-6}
                                                                                padding={[0, 10, 0, 20]}
                                                                                barWidth={30}
                                                                                dataset={[item]}
                                                                                height={barHeight2}
                                                                                loading={false}
                                                                                chartContainer={`attribution-drillDown-2-${index}`}
                                                                                fields={['category', 'sales']}
                                                                                intervalLabelTooltipKey={'labelSales'}
                                                                                minBound={scaleMinValue(
                                                                                    50,
                                                                                    chart2Data?.minVal?.[
                                                                                        absTile[1]?.key
                                                                                    ],
                                                                                )}
                                                                                maxBound={scaleMaxValue(
                                                                                    50,
                                                                                    chart2Data?.maxVal?.[
                                                                                        absTile[1]?.key
                                                                                    ],
                                                                                )}
                                                                                intervalFormatter={(val: any) => {
                                                                                    return absTile[1].key ===
                                                                                        'percentage'
                                                                                        ? formatNumWithKMBAlt(
                                                                                              val,
                                                                                              decimalForPercentage,
                                                                                          ) + '%'
                                                                                        : currency +
                                                                                              formatNumWithKMBAlt(
                                                                                                  val,
                                                                                                  decimalForNonPercentage,
                                                                                                  decimalForNonPercentage,
                                                                                              );
                                                                                }}
                                                                                tooltipFormatter={(xAxis, val) => {
                                                                                    return {
                                                                                        name: xAxis,
                                                                                        value:
                                                                                            absTile[1].key ===
                                                                                            'percentage'
                                                                                                ? formatNumWithKMBAlt(
                                                                                                      val,
                                                                                                      decimalForPercentage,
                                                                                                  ) + '%'
                                                                                                : currency +
                                                                                                  formatNumWithKMBAlt(
                                                                                                      val,
                                                                                                      decimalForNonPercentage,
                                                                                                      decimalForNonPercentage,
                                                                                                  ),
                                                                                    };
                                                                                }}
                                                                                width={chartWidth - yAxisGraphWidth}
                                                                                transpose
                                                                                Xline={false}
                                                                                colorKey={'color'}
                                                                                intervalStyle={{
                                                                                    cursor: disableClickEvent
                                                                                        ? 'auto'
                                                                                        : 'pointer',
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            );
                                        }}
                                    </AutoSizer>
                                </div>
                            </div>
                            {/* X AXIS GRAPH COMPONENT */}
                            <Barchart
                                additionalStyle={{ marginLeft: yAxisGraphWidth - 10 }}
                                padding={[0, 10, 20, 20]}
                                dataset={[]}
                                height={50}
                                loading={false}
                                chartContainer={`attribution-drillDown-X-2-${1}`}
                                fields={['category', 'sales']}
                                intervalLabelTooltipKey={'labelSales'}
                                minBound={scaleMinValue(50, chart2Data?.minVal?.[absTile[1]?.key])}
                                maxBound={scaleMaxValue(50, chart2Data?.maxVal?.[absTile[1]?.key])}
                                width={chartWidth - yAxisGraphWidth + 20}
                                transpose
                                yAxisLabelFormatter={(val: any) => {
                                    return absTile[1].key === 'percentage'
                                        ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                        : formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                                }}
                                YGrid={false}
                                xAxisConfig={false}
                                colorKey={'color'}
                            />
                        </>
                        <XAxisLabel absTile={volTile} appConfig={appConfig} />
                    </div>
                )}
            </div>
        </section>
    );
};

export default SalesAttributionDrillDownChart;

export const XAxisLabel = ({ absTile, appConfig }: { absTile: any; appConfig: any }) => {
    return <p className={`text-center relative ${yAxisLabelClass}`}>{absTile?.label} </p>;
};
